<template>
	<app-header-teleport>
		<div class="flex flex-1 items-center">
			<app-title :title="$t('financePurchases')" />
			<div class="ml-2">
				<app-advanced-filter @onFilter="onFilter" @onClear="onClear" :loading="isLoading">
					<app-advanced-date-filter v-model="filters" :start-date="'startDate'" :end-date="'endDate'" />
				</app-advanced-filter>
			</div>
		</div>
	</app-header-teleport>
	<app-section class="tight mt-4">
		<div class="flex">
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="localStatus === LOCAL_ALL ? 'border-primary' : 'border-border-color'"
				@click="localStatus = LOCAL_ALL"
			>
				{{ $t('all') }}
			</div>
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="localStatus === LOCAL_PAID ? 'border-primary' : 'border-border-color'"
				@click="localStatus = LOCAL_PAID"
			>
				{{ $t('paid') }}
			</div>
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="localStatus === LOCAL_UNPAID ? 'border-primary' : 'border-border-color'"
				@click="localStatus = LOCAL_UNPAID"
			>
				{{ $t('unpaid') }}
			</div>
		</div>
		<app-table :columns="columns" :data="purchases" :expandable="true" :one-at-a-time="true">
			<template v-slot:id="{ item }">
				<span>#{{ item.id }}</span>
				<span class="text-white text-10 px-1 py-px rounded-md ml-1" :class="paymentStatusClass(item)">
					{{ isPaid(item) ? $t('paid') : $t('unpaid') }}
				</span>
			</template>
			<template v-slot:docDate="{ item }">
				{{ $filters.formatShortDate(item['docDate']) }}
			</template>
			<template v-slot:invoiceStatus="{ item }">
				<span>
					{{ item['invoiceStatus'] }}
				</span>
			</template>
			<template v-slot:paymentType="{ item }">
				<span>{{ item['paymentType'] || 'n/d' }}</span>
			</template>
			<template v-slot:restaurantName="{ item }">
				{{ item.business?.restaurantName }}
			</template>
			<template v-slot:officialNo="{ item }">
				<span v-if="item['officialNo']">{{ item['officialNo'] }}</span>
				<span v-else>n/d</span>
			</template>
			<template v-slot:fiscalizationUrl="{ item }">
				<a v-if="item['fiscalizationUrl']" :href="item['fiscalizationUrl']" target="_blank" @click.stop="">
					<span class="text-primary hover:underline">View</span>
				</a>
				<span v-else>n/d</span>
			</template>
			<template v-slot:officialDate="{ item }">
				<span v-if="item['officialNo']">{{ $filters.formatShortDate(item['officialDate']) }}</span>
				<span v-else>n/d</span>
			</template>
			<template v-slot:subtotal="{ item }">{{
				$filters.formatCurrency(item['subtotal'], item.business?.currency)
			}}</template>
			<template v-slot:vat="{ item }">{{
				$filters.formatCurrency(item['vat'], item.business?.currency)
			}}</template>
			<template v-slot:total="{ item }">{{
				$filters.formatCurrency(item['total'], item.business?.currency)
			}}</template>
			<template v-slot:paid="{ item }">{{
				$filters.formatCurrency(item['paid'], item.business?.currency)
			}}</template>
			<template v-slot:unpaid="{ item }">{{
				$filters.formatCurrency(item['unpaid'], item.business?.currency)
			}}</template>
			<template v-slot:appDetails="{ item }">
				<div class="flex flex-1">
					<div class="flex flex-1">
						<div class="flex flex-col px-4 border-r-1 border-dashed border-border-color">
							<p class="py-1">
								<span class="font-bold">NUMBER: </span>
								<span>{{ item.officialNo ? `#${item.officialNo}` : 'n/d' }}</span>
							</p>
							<p class="py-1">
								<span class="font-bold">INVOICE DATE: </span>
								<span>{{ $filters.formatShortDate(item.docDate) }}</span>
							</p>
							<p class="py-1">
								<span class="font-bold">DUE DATE: </span>
								<span>{{ item.dueDate ? $filters.formatShortDate(item.dueDate) : 'n/d' }}</span>
							</p>
						</div>
					</div>
					<div class="flex flex-col pl-4 py-2 border-l-1 border-dashed border-border-color">
						<div class="py-2">
							<p class="font-bold">PAID</p>
							<p class="text-success text-xl">
								{{ $filters.formatCurrency(item['paid'], item.business?.currency) }}
							</p>
						</div>
						<div class="py-2">
							<p class="font-bold">UNPAID</p>
							<p class="text-warning text-xl">
								{{ $filters.formatCurrency(item['unpaid'], item.business?.currency) }}
							</p>
						</div>
					</div>
				</div>
			</template>
		</app-table>
		<app-pagination
			:filters="searchFilters"
			@prevPage="prevPage"
			@nextPage="nextPage"
			@limitChange="changeLimit"
			:loading="isLoading"
		/>
	</app-section>
</template>

<script>
	import AppTitle from '@/components/app/AppTitle';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter';
	import AppTable from '@/components/app/AppTable';
	import AppPagination from '@/components/app/AppPagination';
	import constants from '@/constants/finance';
	import FinanceService from '@/services/FinanceService';
	import { mapGetters } from 'vuex';

	const LOCAL_ALL = '';
	const LOCAL_PAID = 'LOCAL_PAID';
	const LOCAL_UNPAID = 'LOCAL_UNPAID';

	const defaultFilters = () => ({
		limit: constants.defaultFilters.limit,
		offset: 0,
		startDate: constants.defaultFilters.startDate,
		endDate: constants.defaultFilters.endDate,
	});

	export default {
		name: 'Purchases',
		components: {
			AppTitle,
			AppAdvancedFilter,
			AppAdvancedDateFilter,
			AppTable,
			AppPagination,
		},
		data: () => ({
			LOCAL_ALL,
			LOCAL_PAID,
			LOCAL_UNPAID,
			localStatus: LOCAL_ALL,
			isLoading: false,
			filters: defaultFilters(),
			searchFilters: defaultFilters(),
			columns: constants.purchaseColumns,
			statusColorMap: constants.purchaseStatusColorMap,
			allPurchases: [],
		}),
		computed: {
			...mapGetters({
				selectedRestaurants: 'selectedRestaurants',
			}),
			purchases() {
				if (!this.localStatus) {
					return this.allPurchases;
				}
				return this.allPurchases.filter((p) =>
					this.localStatus === LOCAL_PAID ? this.isPaid(p) : !this.isPaid(p)
				);
			},
		},
		methods: {
			isPaid(purchase) {
				return purchase && purchase['unPaidAmountCents'] <= 0;
			},
			paymentStatusClass(purchase) {
				if (purchase.paidAmountCents === 0 && purchase.amountWithVatCents > 0) {
					return 'bg-primary';
				}
				if (purchase.paidAmountCents >= purchase.amountWithVatCents && purchase.amountWithVatCents > 0) {
					return 'bg-success';
				}
				if (purchase.paidAmountCents > 0 && purchase.paidAmountCents < purchase.amountWithVatCents) {
					return 'bg-warning';
				}
				return 'bg-secondary';
			},
			getPurchases() {
				this.isLoading = true;
				FinanceService.getPurchases({
					...this.searchFilters,
					businessIDs: this.selectedRestaurants.join('-'),
				})
					.then((purchases) => {
						this.allPurchases = purchases;
					})
					.catch((e) => this.$notificationService.error(e))
					.finally(() => {
						this.isLoading = false;
						this.emitter.emit('isLoading', false);
					});
			},
			onFilter() {
				this.searchFilters = { ...this.filters, offset: 0 };
				this.refresh();
			},
			onClear() {
				this.filters = defaultFilters();
				this.searchFilters = defaultFilters();
				this.refresh();
			},
			prevPage() {
				this.searchFilters.offset = Math.max(this.searchFilters.offset - this.searchFilters.limit, 0);
				this.getPurchases();
			},
			nextPage() {
				this.searchFilters.offset = this.searchFilters.offset + this.searchFilters.limit;
				this.getPurchases();
			},
			changeLimit(value) {
				this.searchFilters = {
					...this.searchFilters,
					limit: value,
					offset: 0,
				};
				this.getPurchases();
			},
			refresh() {
				this.getPurchases();
			},
		},
		mounted() {
			this.emitter.emit('isLoading', true);
			this.refresh();
		},
		watch: {
			selectedRestaurants() {
				if (!this.$store.getters.token) return;
				this.searchFilters.offset = 0;
				this.refresh();
			},
		},
	};
</script>
